"use strict";
import { CurrencyAmount, TradeType } from "@uniswap/sdk-core";
import { Field } from "components/swap/constants";
import { CHAIN_IDS_TO_NAMES, useSupportedChainId } from "constants/chains";
import { NATIVE_CHAIN_ID } from "constants/tokens";
import { useCurrency, useCurrencyInfo } from "hooks/Tokens";
import { useAccount } from "hooks/useAccount";
import useAutoSlippageTolerance from "hooks/useAutoSlippageTolerance";
import { useDebouncedTrade } from "hooks/useDebouncedTrade";
import useParsedQueryString from "hooks/useParsedQueryString";
import { useSwapTaxes } from "hooks/useSwapTaxes";
import { useUSDPrice } from "hooks/useUSDPrice";
import useNativeCurrency from "lib/hooks/useNativeCurrency";
import tryParseCurrencyAmount from "lib/utils/tryParseCurrencyAmount";
import { useCallback, useEffect, useMemo } from "react";
import { useCurrencyBalance, useCurrencyBalances } from "state/connection/hooks";
import { isClassicTrade, isSubmittableTrade, isUniswapXTrade } from "state/routing/utils";
import { useSwapAndLimitContext, useSwapContext } from "state/swap/useSwapContext";
import { useUserSlippageToleranceWithDefault } from "state/user/hooks";
import { useTokenProjects } from "uniswap/src/features/dataApi/tokenProjects";
import { FeatureFlags } from "uniswap/src/features/gating/flags";
import { useFeatureFlag } from "uniswap/src/features/gating/hooks";
import { Trans } from "uniswap/src/i18n";
import { UniverseChainId } from "uniswap/src/types/chains";
import { areCurrencyIdsEqual, currencyId } from "uniswap/src/utils/currencyId";
import { isAddress } from "utilities/src/addresses";
import { getParsedChainId } from "utils/chains";
export function useSwapActionHandlers() {
  const { swapState, setSwapState } = useSwapContext();
  const { currencyState, setCurrencyState } = useSwapAndLimitContext();
  const inputTokenProjects = useTokenProjects(
    currencyState.inputCurrency ? [currencyId(currencyState.inputCurrency)] : []
  );
  const outputTokenProjects = useTokenProjects(
    currencyState.outputCurrency ? [currencyId(currencyState.outputCurrency)] : []
  );
  const onCurrencySelection = useCallback(
    (field, currency) => {
      const [currentCurrencyKey, otherCurrencyKey] = field === Field.INPUT ? ["inputCurrency", "outputCurrency"] : ["outputCurrency", "inputCurrency"];
      const otherCurrency = currencyState[otherCurrencyKey];
      if (otherCurrency && currency.equals(otherCurrency)) {
        setCurrencyState({
          [currentCurrencyKey]: currency,
          [otherCurrencyKey]: currencyState[currentCurrencyKey]
        });
        setSwapState((swapState2) => ({
          ...swapState2,
          independentField: swapState2.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT
        }));
      } else if (otherCurrency?.chainId !== currency.chainId) {
        const otherCurrencyTokenProjects = field === Field.INPUT ? outputTokenProjects : inputTokenProjects;
        const otherCurrency2 = otherCurrencyTokenProjects?.data?.find(
          (project) => project?.currency.chainId === currency.chainId
        );
        setCurrencyState((state) => ({
          ...state,
          [currentCurrencyKey]: currency,
          [otherCurrencyKey]: otherCurrency2 && !areCurrencyIdsEqual(currencyId(currency), otherCurrency2.currencyId) ? otherCurrency2.currency : void 0
        }));
      } else {
        setCurrencyState((state) => ({
          ...state,
          [currentCurrencyKey]: currency
        }));
      }
    },
    [currencyState, inputTokenProjects, outputTokenProjects, setCurrencyState, setSwapState]
  );
  const onSwitchTokens = useCallback(
    ({
      newOutputHasTax,
      previouslyEstimatedOutput
    }) => {
      if (newOutputHasTax && swapState.independentField === Field.INPUT) {
        setSwapState((swapState2) => ({
          ...swapState2,
          typedValue: previouslyEstimatedOutput
        }));
      } else {
        setSwapState((prev) => ({
          ...prev,
          independentField: prev.independentField === Field.INPUT ? Field.OUTPUT : Field.INPUT
        }));
      }
      setCurrencyState((prev) => ({
        inputCurrency: prev.outputCurrency,
        outputCurrency: prev.inputCurrency
      }));
    },
    [setCurrencyState, setSwapState, swapState.independentField]
  );
  const onUserInput = useCallback(
    (field, typedValue) => {
      setSwapState((state) => {
        return {
          ...state,
          independentField: field,
          typedValue
        };
      });
    },
    [setSwapState]
  );
  return {
    onSwitchTokens,
    onCurrencySelection,
    onUserInput
  };
}
export function useDerivedSwapInfo(state) {
  const account = useAccount();
  const { chainId, currencyState } = useSwapAndLimitContext();
  const nativeCurrency = useNativeCurrency(chainId);
  const balance = useCurrencyBalance(account.address, nativeCurrency);
  const inputCurrencyInfo = useCurrencyInfo(currencyState.inputCurrency);
  const outputCurrencyInfo = useCurrencyInfo(currencyState.outputCurrency);
  const inputCurrency = inputCurrencyInfo?.currency;
  const outputCurrency = outputCurrencyInfo?.currency;
  const { independentField, typedValue } = state;
  const { inputTax, outputTax } = useSwapTaxes(
    inputCurrency?.isToken ? inputCurrency.address : void 0,
    outputCurrency?.isToken ? outputCurrency.address : void 0,
    chainId
  );
  const relevantTokenBalances = useCurrencyBalances(
    account.address,
    useMemo(() => [inputCurrency ?? void 0, outputCurrency ?? void 0], [inputCurrency, outputCurrency])
  );
  const isExactIn = independentField === Field.INPUT;
  const parsedAmount = useMemo(
    () => tryParseCurrencyAmount(typedValue, (isExactIn ? inputCurrency : outputCurrency) ?? void 0),
    [inputCurrency, isExactIn, outputCurrency, typedValue]
  );
  const trade = useDebouncedTrade(
    isExactIn ? TradeType.EXACT_INPUT : TradeType.EXACT_OUTPUT,
    parsedAmount,
    (isExactIn ? outputCurrency : inputCurrency) ?? void 0,
    state.routerPreferenceOverride,
    account.address
  );
  const { data: nativeCurrencyBalanceUSD } = useUSDPrice(balance, nativeCurrency);
  const { data: outputFeeFiatValue } = useUSDPrice(
    isSubmittableTrade(trade.trade) && trade.trade.swapFee ? CurrencyAmount.fromRawAmount(trade.trade.outputAmount.currency, trade.trade.swapFee.amount) : void 0,
    trade.trade?.outputAmount.currency
  );
  const currencyBalances = useMemo(
    () => ({
      [Field.INPUT]: relevantTokenBalances[0],
      [Field.OUTPUT]: relevantTokenBalances[1]
    }),
    [relevantTokenBalances]
  );
  const currencies = useMemo(
    () => ({
      [Field.INPUT]: inputCurrency,
      [Field.OUTPUT]: outputCurrency
    }),
    [inputCurrency, outputCurrency]
  );
  const classicAutoSlippage = useAutoSlippageTolerance(isClassicTrade(trade.trade) ? trade.trade : void 0);
  const uniswapXAutoSlippage = isUniswapXTrade(trade.trade) ? trade.trade.slippageTolerance : void 0;
  const autoSlippage = uniswapXAutoSlippage ?? classicAutoSlippage;
  const classicAllowedSlippage = useUserSlippageToleranceWithDefault(autoSlippage);
  const allowedSlippage = uniswapXAutoSlippage ?? classicAllowedSlippage;
  const insufficientGas = chainId && [
    UniverseChainId.Zero,
    UniverseChainId.BOB,
    UniverseChainId.CYBER,
    UniverseChainId.SHAPE,
    UniverseChainId.INK,
    UniverseChainId.REDSTONE,
    UniverseChainId.REDSTONE_GARNET,
    UniverseChainId.AbstractMainnet,
    UniverseChainId.AnimeTestnet,
    UniverseChainId.Anime,
    UniverseChainId.Mode,
    UniverseChainId.FlowTestnet,
    UniverseChainId.FlowMainnet
  ].includes(chainId) ? false : isClassicTrade(trade.trade) && (nativeCurrencyBalanceUSD ?? 0) < (trade.trade.totalGasUseEstimateUSDWithBuffer ?? 0);
  const { isDisconnected } = useAccount();
  const inputError = useMemo(() => {
    let inputError2;
    if (!account.isConnected) {
      inputError2 = isDisconnected ? <Trans i18nKey="common.connectWallet.button" /> : <Trans i18nKey="common.connectingWallet" />;
    }
    if (!currencies[Field.INPUT] || !currencies[Field.OUTPUT]) {
      inputError2 = inputError2 ?? <Trans i18nKey="common.selectToken.label" />;
    }
    if (!parsedAmount) {
      inputError2 = inputError2 ?? <Trans i18nKey="common.noAmount.error" />;
    }
    if (insufficientGas) {
      inputError2 = <Trans
        i18nKey="common.insufficientTokenBalance.error"
        values={{
          tokenSymbol: nativeCurrency.symbol
        }}
      />;
    }
    const [balanceIn, maxAmountIn] = [currencyBalances[Field.INPUT], trade?.trade?.maximumAmountIn(allowedSlippage)];
    if (balanceIn && maxAmountIn && balanceIn.lessThan(maxAmountIn)) {
      inputError2 = <Trans
        i18nKey="common.insufficientTokenBalance.error"
        values={{
          tokenSymbol: balanceIn.currency.symbol
        }}
      />;
    }
    return inputError2;
  }, [
    account.isConnected,
    currencies,
    parsedAmount,
    currencyBalances,
    trade?.trade,
    allowedSlippage,
    isDisconnected,
    insufficientGas,
    nativeCurrency.symbol
  ]);
  return useMemo(
    () => ({
      currencies,
      currencyBalances,
      parsedAmount,
      inputError,
      trade,
      autoSlippage,
      allowedSlippage,
      outputFeeFiatValue,
      inputTax,
      outputTax
    }),
    [
      allowedSlippage,
      autoSlippage,
      currencies,
      currencyBalances,
      inputError,
      outputFeeFiatValue,
      parsedAmount,
      trade,
      inputTax,
      outputTax
    ]
  );
}
function parseFromURLParameter(urlParam) {
  if (typeof urlParam === "string") {
    return urlParam;
  }
  return void 0;
}
function parseCurrencyFromURLParameter(urlParam) {
  if (typeof urlParam === "string") {
    const valid = isAddress(urlParam);
    if (valid) {
      return valid;
    }
    const upper = urlParam.toUpperCase();
    if (upper === "ETH") {
      return "ETH";
    }
    if (urlParam === NATIVE_CHAIN_ID) {
      return NATIVE_CHAIN_ID;
    }
  }
  return void 0;
}
export function serializeSwapStateToURLParameters(state) {
  const { inputCurrency, outputCurrency, typedValue, independentField, chainId } = state;
  const params = new URLSearchParams();
  params.set("chain", CHAIN_IDS_TO_NAMES[chainId]);
  if (inputCurrency) {
    params.set("inputCurrency", inputCurrency.isNative ? NATIVE_CHAIN_ID : inputCurrency.address);
  }
  if (outputCurrency) {
    params.set("outputCurrency", outputCurrency.isNative ? NATIVE_CHAIN_ID : outputCurrency.address);
  }
  const hasValidInput = (inputCurrency || outputCurrency) && typedValue;
  if (hasValidInput) {
    params.set("value", typedValue);
  }
  if (hasValidInput && independentField) {
    params.set("field", independentField);
  }
  return "?" + params.toString();
}
export function queryParametersToCurrencyState(parsedQs) {
  const inputCurrencyId = parseCurrencyFromURLParameter(parsedQs.inputCurrency ?? parsedQs.inputcurrency);
  const parsedOutputCurrencyId = parseCurrencyFromURLParameter(parsedQs.outputCurrency ?? parsedQs.outputcurrency);
  const outputCurrencyId = parsedOutputCurrencyId === inputCurrencyId ? void 0 : parsedOutputCurrencyId;
  const hasCurrencyInput = inputCurrencyId || outputCurrencyId;
  const value = hasCurrencyInput ? parseFromURLParameter(parsedQs.value) : void 0;
  const field = value ? parseFromURLParameter(parsedQs.field) : void 0;
  const chainId = getParsedChainId(parsedQs);
  return {
    inputCurrencyId,
    outputCurrencyId,
    value,
    field,
    chainId
  };
}
export function useInitialCurrencyState() {
  const multichainUXEnabled = useFeatureFlag(FeatureFlags.MultichainUX);
  const { chainId, setIsUserSelectedToken } = useSwapAndLimitContext();
  const parsedQs = useParsedQueryString();
  const parsedCurrencyState = useMemo(() => {
    return queryParametersToCurrencyState(parsedQs);
  }, [parsedQs]);
  const supportedChainId = useSupportedChainId(parsedCurrencyState.chainId ?? chainId) ?? UniverseChainId.FlowMainnet;
  const hasCurrencyQueryParams = parsedCurrencyState.inputCurrencyId || parsedCurrencyState.outputCurrencyId || parsedCurrencyState.chainId;
  useEffect(() => {
    if (parsedCurrencyState.inputCurrencyId || parsedCurrencyState.outputCurrencyId) {
      setIsUserSelectedToken(true);
    }
  }, [parsedCurrencyState.inputCurrencyId, parsedCurrencyState.outputCurrencyId, setIsUserSelectedToken]);
  const { initialInputCurrencyAddress, initialChainId } = useMemo(() => {
    if (multichainUXEnabled && !hasCurrencyQueryParams) {
      return {
        initialInputCurrencyAddress: "ETH",
        initialChainId: UniverseChainId.Mainnet
      };
    }
    if (parsedCurrencyState.inputCurrencyId) {
      return {
        initialInputCurrencyAddress: parsedCurrencyState.inputCurrencyId,
        initialChainId: supportedChainId
      };
    }
    return {
      initialInputCurrencyAddress: parsedCurrencyState.outputCurrencyId ? void 0 : "ETH",
      initialChainId: supportedChainId
    };
  }, [
    hasCurrencyQueryParams,
    multichainUXEnabled,
    parsedCurrencyState.inputCurrencyId,
    parsedCurrencyState.outputCurrencyId,
    supportedChainId
  ]);
  const initialOutputCurrencyAddress = useMemo(
    () => initialInputCurrencyAddress === parsedCurrencyState.outputCurrencyId ? void 0 : parsedCurrencyState.outputCurrencyId,
    [initialInputCurrencyAddress, parsedCurrencyState.outputCurrencyId]
  );
  const initialInputCurrency = useCurrency(initialInputCurrencyAddress, initialChainId);
  const initialOutputCurrency = useCurrency(initialOutputCurrencyAddress, initialChainId);
  const initialTypedValue = initialInputCurrency || initialOutputCurrency ? parsedCurrencyState.value : void 0;
  const initialField = initialTypedValue && parsedCurrencyState.field && parsedCurrencyState.field in Field ? Field[parsedCurrencyState.field] : void 0;
  return {
    initialInputCurrency,
    initialOutputCurrency,
    initialTypedValue,
    initialField,
    initialChainId,
    initialCurrencyLoading: false
  };
}
